/**
 * 올인원 — 회원요약 탭
 * docs/tools_all.md §6
 *
 * - 6-1 회원 카드 (Descriptions, 마스킹 미적용 + 복사 버튼)
 * - 6-2 가족 테이블 + 자녀 CRUD + 방문이력 Drawer
 * - 6-3 쓰기 후 refresh() + markDirty('family')
 */

import React, { useState } from 'react';
import {
  Table, Tag, Button, Space, Modal, Drawer, Input, DatePicker,
  Popconfirm, App as AntApp, Empty, Spin, message as messageApi, Typography,
} from 'antd';
import {
  CopyOutlined, UserAddOutlined, EditOutlined, DeleteOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  addChildMember, updateMember, removeChildMember, getVisitHistory,
} from '../../services/api';

const { Text } = Typography;

const getDisplayName = (m) => {
  if (!m) return '-';
  if (m.nickname) return m.nickname;
  if (m.name) return m.name;
  if (m.member_type === 'CHILD') return '자녀';
  const phone = m.phone || m.phone_masked || '';
  const last4 = phone.replace(/\D/g, '').slice(-4);
  return last4 || '이름 없음';
};

export default function SummaryTab({
  scannedMember, primary, family, passportMap,
  refresh, markDirty, beginAction, endAction, onLog,
}) {
  const { modal } = AntApp.useApp();
  const [childModal, setChildModal] = useState(null); // { mode:'add'|'edit', target?, form }
  const [saving, setSaving] = useState(false);
  const [visitDrawer, setVisitDrawer] = useState({ open: false, loading: false, visits: [], error: null });

  if (!scannedMember) return <Empty description="회원을 검색 해 주세요" />;

  const parentId = primary?.member_id || scannedMember.member_id;
  const isChildScan = primary && scannedMember.member_id !== primary.member_id;
  const balanceSelf = scannedMember.balance ?? scannedMember.coin_balance ?? 0;
  const balanceParent = primary ? (primary.balance ?? primary.coin_balance ?? 0) : null;
  const familyCoinTotal = family.reduce((s, m) => s + (m.balance ?? m.coin_balance ?? 0), 0);
  const myPassport = passportMap[scannedMember.member_id];

  const copyPhone = () => {
    const phone = scannedMember.phone || ('010' + (scannedMember.phone_masked || '').replace(/\D/g, '').slice(-8));
    if (!phone) return;
    navigator.clipboard?.writeText(phone);
    messageApi.success('전화번호 복사됨');
  };

  const openAdd = () => setChildModal({ mode: 'add', form: { nickname: '', birthday: null } });
  const openEdit = (child) => setChildModal({
    mode: 'edit', target: child,
    form: { nickname: child.nickname || child.name || '', birthday: child.birthday ? dayjs(child.birthday) : null },
  });

  const handleSubmitChild = async () => {
    if (!childModal) return;
    const { mode, target, form } = childModal;
    const nickname = form.nickname.trim();
    if (!nickname) { messageApi.warning('별명을 입력하세요'); return; }
    const birthday = form.birthday ? form.birthday.format('YYYY-MM-DD') : null;
    const actionId = `child-${mode}-${Date.now()}`;
    setSaving(true);
    beginAction(actionId);
    try {
      if (mode === 'add') {
        await addChildMember(parentId, { nickname, name: null, birthday });
        onLog?.(`✅ 자녀 '${nickname}' 추가`);
      } else {
        await updateMember(target.member_id, { nickname, birthday });
        onLog?.(`✅ 자녀 '${nickname}' 수정`);
      }
      setChildModal(null);
      await refresh();
      markDirty('family');
    } catch (err) {
      messageApi.error(err?.message || '저장 실패');
    } finally {
      endAction(actionId);
      setSaving(false);
    }
  };

  const handleDeleteChild = async (child) => {
    const name = getDisplayName(child);
    const actionId = `child-del-${child.member_id}`;
    beginAction(actionId);
    try {
      await removeChildMember(parentId, child.member_id);
      onLog?.(`✅ 자녀 '${name}' 삭제 (잔액 이전됨)`);
      await refresh();
      markDirty('family');
    } catch (err) {
      messageApi.error(err?.message || '삭제 실패');
    } finally {
      endAction(actionId);
    }
  };

  const openVisitDrawer = async () => {
    setVisitDrawer({ open: true, loading: true, visits: [], error: null });
    try {
      const res = await getVisitHistory(parentId, 1, 100);
      setVisitDrawer({ open: true, loading: false, visits: res.visits || [], error: null });
    } catch (err) {
      setVisitDrawer({ open: true, loading: false, visits: [], error: err?.message || '방문이력 조회 실패' });
    }
  };

  const familyRows = family.length ? family : [scannedMember];

  return (
    <div className="summary-tab">
      {/* 헤로 카드 — 이름 + 전화 + 통계 그리드 */}
      <div className="summary-hero">
        <div className="summary-hero-head">
          <div className="summary-hero-name-wrap">
            <div className="summary-hero-name">{getDisplayName(scannedMember)}</div>
            <div className="summary-hero-tags">
              {isChildScan
                ? <Tag color="purple">자녀 · 보호자 {getDisplayName(primary)}</Tag>
                : <Tag color="blue">보호자</Tag>}
              {myPassport ? <Tag color="purple">✅ VIP</Tag> : <Tag>❌ 여권미발급</Tag>}
            </div>
          </div>
          <div className="summary-hero-phone">
            <span className="summary-phone-text">{scannedMember.phone || scannedMember.phone_masked || '-'}</span>
            {(scannedMember.phone || scannedMember.phone_masked) && (
              <Button size="small" type="text" icon={<CopyOutlined />} onClick={copyPhone} />
            )}
          </div>
        </div>
        <div className="summary-stat-grid">
          <div className="summary-stat-cell">
            <div className="summary-stat-label">잔액</div>
            <div className="summary-stat-value summary-stat-coin">🪙 {balanceSelf}</div>
            {isChildScan && <div className="summary-stat-sub">보호자 {balanceParent}</div>}
          </div>
          <div className="summary-stat-cell">
            <div className="summary-stat-label">가족 합계</div>
            <div className="summary-stat-value summary-stat-coin">🪙 {familyCoinTotal}</div>
          </div>
          <div className="summary-stat-cell">
            <div className="summary-stat-label">총 방문</div>
            <div className="summary-stat-value">{scannedMember.total_visits ?? scannedMember.visit_count ?? '-'}</div>
            <div className="summary-stat-sub">최근 {scannedMember.last_visit_at?.slice(0, 10) || '-'}</div>
          </div>
          <div className="summary-stat-cell">
            <div className="summary-stat-label">가입일</div>
            <div className="summary-stat-value summary-stat-date">{scannedMember.created_at?.slice(0, 10) || '-'}</div>
            <div className="summary-stat-sub">ID {scannedMember.member_id}</div>
          </div>
        </div>
      </div>

      {/* 가족 카드 리스트 */}
      <div className="summary-family-section">
        <div className="summary-family-head">
          <Text strong>가족 {familyRows.length}명</Text>
          <Space size={4}>
            <Button size="small" type="primary" icon={<UserAddOutlined />} onClick={openAdd}>자녀 추가</Button>
            <Button size="small" icon={<HistoryOutlined />} onClick={openVisitDrawer}>방문이력</Button>
          </Space>
        </div>
        <div className="summary-family-list">
          {familyRows.map((m) => {
            const isMe = m.member_id === scannedMember.member_id;
            const isChild = m.member_type === 'CHILD';
            return (
              <div key={m.member_id} className={`summary-family-card${isMe ? ' is-me' : ''}`}>
                <Tag color={isChild ? 'purple' : 'blue'} className="summary-family-role">
                  {isChild ? '자녀' : '보호자'}
                </Tag>
                <div className="summary-family-name">
                  {getDisplayName(m)}
                  {isMe && <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>(현재)</Text>}
                </div>
                <Tag color="gold" className="summary-family-coin">🪙 {m.balance ?? m.coin_balance ?? 0}</Tag>
                {passportMap[m.member_id]
                  ? <Tag color="purple" className="summary-family-passport">VIP</Tag>
                  : <Tag className="summary-family-passport">—</Tag>}
                {isChild && (
                  <Space size={4} className="summary-family-actions">
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(m)} />
                    <Popconfirm
                      title={`'${getDisplayName(m)}' 삭제`}
                      description="잔액은 보호자에게 이전됩니다"
                      okText="삭제" cancelText="취소" okButtonProps={{ danger: true }}
                      onConfirm={() => handleDeleteChild(m)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Modal
        open={!!childModal}
        title={childModal?.mode === 'add' ? '자녀 추가' : '자녀 수정'}
        onCancel={() => setChildModal(null)}
        onOk={handleSubmitChild}
        confirmLoading={saving}
        okText="저장" cancelText="취소"
        destroyOnClose
      >
        {childModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <div style={{ marginBottom: 4 }}>별명 *</div>
              <Input
                size="small"
                value={childModal.form.nickname}
                onChange={(e) => setChildModal({ ...childModal, form: { ...childModal.form, nickname: e.target.value } })}
                placeholder="예: 첫째"
                maxLength={20}
              />
            </div>
            <div>
              <div style={{ marginBottom: 4 }}>생일 (선택)</div>
              <DatePicker
                size="small"
                value={childModal.form.birthday}
                onChange={(d) => setChildModal({ ...childModal, form: { ...childModal.form, birthday: d } })}
                style={{ width: '100%' }}
                inputReadOnly
              />
            </div>
          </Space>
        )}
      </Modal>

      <Drawer
        open={visitDrawer.open}
        title="방문 이력 (최근 100건)"
        placement="bottom"
        height="60%"
        onClose={() => setVisitDrawer({ ...visitDrawer, open: false })}
      >
        {visitDrawer.loading && <Spin />}
        {visitDrawer.error && <div style={{ color: '#C62828' }}>{visitDrawer.error}</div>}
        {!visitDrawer.loading && !visitDrawer.error && (
          <Table
            size="small"
            rowKey={(r) => r.visit_id || r.id || `${r.visit_date}-${r.store_name}`}
            dataSource={visitDrawer.visits}
            pagination={{ pageSize: 20 }}
            columns={[
              { title: '방문일', dataIndex: 'visit_date', render: (v) => v?.slice(0, 16)?.replace('T', ' ') },
              { title: '매장', dataIndex: 'store_name' },
            ]}
          />
        )}
      </Drawer>
    </div>
  );
}
